/**
 * Valeur sentinelle de l'option « Vide ».
 *
 * ⚠️ Elle ne part JAMAIS telle quelle au moteur : le gestionnaire de sélection la traduit en
 * `IS NULL`, opérateur qui couvre à la fois le `NULL` de PostgreSQL et la chaîne vide — les deux
 * formes que prend une donnée non renseignée. Improbable comme donnée réelle, sur le modèle du
 * `allOptionsOfFilter123456` déjà utilisé par « Tout sélectionner ».
 *
 * ⚠️ **Elle vit ici, et non dans le composant, pour que ce module puisse la nommer sans cycle
 * d'import.** C'est lui qui fabrique les pastilles : sans ça, une sélection « Vide » s'affichait
 * avec la sentinelle en clair — exactement ce que voit l'utilisateur quand personne ne la traduit.
 */
export const VALEUR_VIDE = "__valeurVideDuFiltre123456"

/**
 * Pastilles affichées par une liste déroulante de filtre, d'après les valeurs sélectionnées.
 *
 * ⚠️ Une valeur sélectionnée est TOUJOURS affichée, même si elle a quitté la liste des options.
 * Elle était auparavant écartée quand les options ne la contenaient plus, alors que le filtre
 * restait APPLIQUÉ — il vit dans le magasin de filtres, l'affichage n'en est qu'une vue. Le
 * résultat était un filtre actif et invisible : la page restait restreinte sans que rien à l'écran
 * ne dise pourquoi, et l'utilisateur n'avait aucun moyen de le retirer.
 *
 * ⚠️ Le cas devient courant avec les filtres dépendants (écart E4) : restreindre les entités
 * d'après un organisme fait précisément sortir des valeurs de la liste. Garder la pastille est ce
 * qui rend la cascade réversible.
 *
 * @param value          la sélection courante, telle qu'elle est enregistrée
 * @param libelleBooleen rend « Oui »/« Non », un booléen n'ayant pas de libellé propre
 * @param libelleVide    libellé traduit de l'option « Vide »
 */
// `libelles` : libellés combinés `{ valeur: libellé }` (`Utils/optionLabel`), ou absent — la pastille
// doit dire le même mot que l'option cochée.
export const valeursAffichees = (value, libelleBooleen, libelleVide = "Empty", libelles = null) => {
  if (!Array.isArray(value)) return []
  return value.map(val => ({
    value: val,
    // ⚠️ La sentinelle est une valeur INTERNE : affichée telle quelle, elle apparaissait à
    // l'écran sous la forme `__valeurVideDuFiltre…` dès que la sélection revenait du magasin de
    // filtres. La liste d'options, elle, était bien libellée : les deux chemins doivent traduire.
    label:
      val === VALEUR_VIDE
        ? libelleVide
        : libelles?.[String(val)] ?? (typeof val === "boolean" ? libelleBooleen(val) : val),
  }))
}

/**
 * Modes de restriction des valeurs PROPOSÉES par une liste déroulante de filtre.
 *
 * Réglage `widgetProps.valueFilter = { mode, values }`, posé par l'écran de configuration ou par un
 * modèle de dashboard. Absent, ou `values` vide : toutes les valeurs sont proposées, comme avant.
 */
export const MODES_FILTRE_VALEURS = {
  EXCLURE: "exclude",
  UNIQUEMENT: "include",
}

/**
 * Comparaison d'une valeur de la colonne avec une valeur du réglage.
 *
 * ⚠️ En CHAÎNE, espaces de bord retirés : le réglage est enregistré en JSON et une valeur de la
 * colonne peut revenir en nombre ou en booléen (`2026` contre `"2026"`, `true` contre `"true"`).
 * Une égalité stricte rendrait la règle inopérante sur ces colonnes, sans le moindre message.
 * Pas de pliage de casse : « CRC » et « Crc » sont deux libellés distincts dans un référentiel.
 */
const cleValeur = v => String(v).trim()

/**
 * Restreint les valeurs proposées par un filtre selon son réglage `valueFilter`.
 *
 * ⚠️ Ne touche QUE la liste proposée : tant que rien n'est sélectionné, les fiches portant une
 * valeur écartée restent visibles dans le dashboard. C'est le choix fonctionnel (01/10/2026) — le
 * réglage dit ce que l'utilisateur peut choisir, pas le périmètre de la page. « Tout sélectionner »
 * ne prend que ce qui est proposé, donc sélectionne bien sans les valeurs écartées.
 *
 * ⚠️ En mode « uniquement », une valeur du réglage absente des données n'est PAS ajoutée : proposer
 * un choix qui ne ramène rien est pire que de ne pas le proposer.
 *
 * @param valeurs     les valeurs que le filtre a chargées (vides déjà retirées par l'appelant)
 * @param valueFilter `{ mode: "exclude" | "include", values: [] }`, ou absent
 * @returns un nouveau tableau, dans l'ordre d'entrée
 */
export const restreindreValeurs = (valeurs, valueFilter) => {
  const liste = Array.isArray(valeurs) ? valeurs : []
  const reglees = Array.isArray(valueFilter?.values) ? valueFilter.values : []
  if (reglees.length === 0) return [...liste]
  const cles = new Set(reglees.map(cleValeur))
  if (valueFilter?.mode === MODES_FILTRE_VALEURS.EXCLURE) return liste.filter(v => !cles.has(cleValeur(v)))
  if (valueFilter?.mode === MODES_FILTRE_VALEURS.UNIQUEMENT) return liste.filter(v => cles.has(cleValeur(v)))
  // Mode inconnu : on ne devine pas, on propose tout.
  return [...liste]
}

/**
 * Valeurs DISTINCTES chargées par un filtre, en chaînes, triées — pour l'écran de réglage.
 *
 * Lit TOUTES les colonnes de chaque ligne, comme `formatWidgetFilterBody`, et éclate une cellule
 * multi-valeurs selon `multiValueSeparator`. Les vides sont écartés : ils ne se règlent pas ici
 * (c'est le rôle de l'option « Vide »).
 *
 * ⚠️ Vit ici et non dans `widgetsUtils` : l'écran de configuration n'a pas à charger tout le graphe
 * des widgets pour lister quelques valeurs.
 *
 * @param lignes     `specificWidgetData.data`, la réponse ETL du filtre
 * @param separateur `widgetProps.multiValueSeparator`, ou absent
 */
export const valeursChargees = (lignes, separateur) => {
  const sorties = new Set()
  for (const ligne of Array.isArray(lignes) ? lignes : []) {
    for (const brute of Object.values(ligne ?? {})) {
      for (const v of Array.isArray(brute) ? brute : [brute]) {
        if (v === null || v === undefined || v === "") continue
        const morceaux = typeof v === "string" && separateur ? v.split(separateur) : [v]
        for (const m of morceaux) {
          const propre = String(m).trim()
          if (propre !== "") sorties.add(propre)
        }
      }
    }
  }
  return [...sorties].sort((a, b) => a.localeCompare(b))
}
